import { useEffect, useMemo, useState } from 'react';
import type { SywComponent } from '../hooks/useSywData';
import type { DeviceCombo } from '../store/wizardStore';
import { useWizardStore } from '../store/wizardStore';
import { BrowseMode } from './AddDeviceWizard';
import { EspelhoCombinatorFlow } from './EspelhoCombinatorFlow';
import { NeutralQuestion } from './NeutralQuestion';
import { InfoHint } from './InfoHint';
import { isTomada, teclasOf, friendlyName } from '../lib/catalogModel';
import {
  loadCatalog, sizesAvailable, familiesForSize, techsForFamily, modulesForSlot,
  espelhoFor, moduloBySku, swatchHex, techNeedsHub, techNeedsNeutral,
  SIZE_LABEL, SLOT_LABEL, TECH_LABEL,
  type Catalog, type Family, type EspelhoColor, type ModuleTech,
} from '../lib/espelhoCatalog';

// Guided "adicionar aparelho" modal — the espelho-configurator flow, run INSIDE
// a division. Ported from the espelho-mvp EspelhoConfigPage so the division
// wizard gets the good flow with per-slot module variations:
//   tamanho → acabamento → tecnologia → módulo (variações por posição) → cor → quantidade
// On confirm it produces a DeviceCombo (same contract as the legacy
// AddDeviceWizard) so the division selections / floor-plan / cart pipeline keep
// working unchanged. The "Explorar catálogo" tab reuses the dynamic SYW/Shopify
// browse list ("outras marcas") — it lights up once the Storefront token exists.

type Step = 'tamanho' | 'acabamento' | 'tecnologia' | 'modulo' | 'cor' | 'quantidade';
const STEPS: { key: Step; label: string }[] = [
  { key: 'tamanho', label: 'Tamanho' },
  { key: 'acabamento', label: 'Acabamento' },
  { key: 'tecnologia', label: 'Tecnologia' },
  { key: 'modulo', label: 'Módulo' },
  { key: 'cor', label: 'Cor' },
  { key: 'quantidade', label: 'Quantidade' },
];

const COLORS: EspelhoColor[] = ['Branco', 'Preto', 'Dourado'];

let comboSeq = 0;
function comboId(): string {
  comboSeq += 1;
  return `combo-${Date.now().toString(36)}-${comboSeq}`;
}

function techHint(t: ModuleTech): string {
  switch (t) {
    case 'wifi': return 'Liga ao router Wi-Fi. Sem hub.';
    case 'zigbee-sn': return 'Requer hub Zigbee. Não precisa de neutro.';
    case 'zigbee-cn': return 'Requer hub Zigbee. Requer neutro.';
    case 'zigbee': return 'Requer hub Zigbee.';
    case 'passivo': return 'Sem comunicação — interruptor tradicional.';
  }
}

type WizardMode = 'guided' | 'browse' | 'combinator';

export function EspelhoDeviceWizard({
  catalog,
  onSave,
  onClose,
  initialMode = 'guided',
}: {
  catalog: SywComponent[];
  onSave: (combo: DeviceCombo) => void;
  onClose: () => void;
  initialMode?: WizardMode;
}) {
  const [mode, setMode] = useState<WizardMode>(initialMode);
  const hasNeutral = useWizardStore((s) => s.hasNeutral);

  const [cat, setCat] = useState<Catalog | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('tamanho');

  // Draft state (kept local — no espelhoStore; we emit a DeviceCombo on confirm)
  const [size, setSize] = useState<number | null>(null);
  const [baseSku, setBaseSku] = useState<string | null>(null);
  const [tech, setTech] = useState<ModuleTech | null>(null);
  const [moduleSkus, setModuleSkus] = useState<(string | null)[]>([]);
  const [color, setColor] = useState<EspelhoColor | null>(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    loadCatalog().then(setCat).catch(() => setErr('Não foi possível carregar o catálogo de espelhos. Tenta novamente.'));
  }, []);

  const families = useMemo(
    () => (cat && size ? familiesForSize(cat, size) : []),
    [cat, size],
  );
  const family: Family | null = useMemo(
    () => families.find((f) => f.baseSku === baseSku) ?? null,
    [families, baseSku],
  );
  const colorForResolve: EspelhoColor = color ?? family?.colors[0] ?? 'Branco';
  const techs = useMemo(
    () => (cat && family ? techsForFamily(cat, family, colorForResolve) : []),
    [cat, family, colorForResolve],
  );

  const stepIndex = STEPS.findIndex((s) => s.key === step);

  // ─── Step handlers ─────────────────────────────────────────────────────────
  function pickSize(s: number) {
    setSize(s); setBaseSku(null); setTech(null); setModuleSkus([]); setColor(null);
    setStep('acabamento');
  }
  function pickFamily(f: Family) {
    setBaseSku(f.baseSku); setTech(null);
    setModuleSkus(new Array(f.slotFunctions.length).fill(null)); setColor(null);
    setStep('tecnologia');
  }
  function pickTech(t: ModuleTech) {
    setTech(t);
    setModuleSkus(new Array(family?.slotFunctions.length ?? 0).fill(null));
    setStep('modulo');
  }
  function setModuleForSlot(idx: number, sku: string) {
    setModuleSkus((prev) => {
      const next = [...prev];
      next[idx] = sku;
      return next;
    });
  }
  function pickColor(c: EspelhoColor) {
    setColor(c);
    setStep('quantidade');
  }

  // A slot with a single compatible module has no real choice — pre-select it
  // so the card shows as chosen and "Continuar" enables without an extra tap.
  // (An unselected single-option card read as an already-filled dropdown.)
  useEffect(() => {
    if (step !== 'modulo' || !cat || !family || !tech) return;
    setModuleSkus((prev) => {
      let changed = false;
      const next = family.slotFunctions.map((_, idx) => {
        if (prev[idx] != null) return prev[idx]!;
        const opts = modulesForSlot(cat, family, idx, tech, colorForResolve);
        if (opts.length === 1) { changed = true; return opts[0]!.sku; }
        return prev[idx] ?? null;
      });
      return changed ? next : prev;
    });
  }, [step, cat, family, tech, colorForResolve]);

  const allModulesChosen =
    family != null && moduleSkus.length === family.slotFunctions.length &&
    moduleSkus.every((m) => m != null);

  function back() {
    const order: Step[] = ['tamanho', 'acabamento', 'tecnologia', 'modulo', 'cor', 'quantidade'];
    const i = order.indexOf(step);
    if (i > 0) setStep(order[i - 1]!);
  }

  function confirmUnit() {
    if (!cat || !family || !tech || !color) return;
    const esp = espelhoFor(cat, family.baseSku, color);
    if (!esp) return;
    const chosenModules = moduleSkus.filter((m): m is string => m != null);
    const kind: DeviceCombo['kind'] =
      family.slotFunctions.every((s) => s === 'moldura') ? 'tomada' : 'interruptor';
    const moduleLabels = chosenModules.map((s) => moduloBySku(cat, s)?.name ?? s);
    const label =
      `${family.configLabel} · ${color} · ${TECH_LABEL[tech]}`
      + (moduleLabels.length ? ` (${moduleLabels.length} ${moduleLabels.length === 1 ? 'módulo' : 'módulos'})` : '');
    onSave({
      id: comboId(),
      kind,
      teclas: null,
      boxes: (family.size as 1 | 2 | 3 | 4),
      color,
      label,
      skus: [esp.sku, ...chosenModules],
      qty: Math.max(1, qty),
      tech,
      needsHub: techNeedsHub(tech),
      needsNeutral: techNeedsNeutral(tech),
    });
    onClose();
  }

  // Browse mode: add a single SYW/Shopify component as a simple DeviceCombo.
  function handleBrowseAdd(comp: SywComponent) {
    const inferredKind: DeviceCombo['kind'] = isTomada(comp) ? 'tomada' : 'interruptor';
    onSave({
      id: comboId(),
      kind: inferredKind,
      teclas: isTomada(comp) ? null : (teclasOf(comp) ?? null),
      boxes: 1,
      color: comp.colorName ?? null,
      label: `${friendlyName(comp)}${comp.colorName ? ` · ${comp.colorName}` : ''}`,
      skus: [comp.sku],
      qty: 1,
    });
    onClose();
  }

  const canContinue =
    step === 'cor' ? color !== null
    : step === 'modulo' ? allModulesChosen
    : true;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Adicionar aparelho"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-white sm:max-w-lg sm:rounded-smartify"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-smartify-line px-4 py-3">
          {mode === 'guided' && step !== 'tamanho' && (
            <button
              type="button"
              onClick={back}
              aria-label="Passo anterior"
              className="-ml-1 p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink"
            >
              ←
            </button>
          )}
          <div className="min-w-0 flex-1">
            {mode === 'guided' && (
              <p className="text-xs text-smartify-muted">Passo {stepIndex + 1} de {STEPS.length}</p>
            )}
            <h2 className="truncate text-sm font-semibold text-smartify-ink">
              {mode === 'guided' ? STEPS[stepIndex]!.label
                : mode === 'combinator' ? 'Configurar à medida'
                : 'Explorar catálogo'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink"
          >
            ✕
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex border-b border-smartify-line">
          {([
            ['guided', 'Guiado'],
            ['combinator', 'À medida'],
            ['browse', 'Outras marcas'],
          ] as [WizardMode, string][]).map(([m, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={[
                'flex-1 py-2 text-xs font-semibold transition-colors',
                mode === m
                  ? 'border-b-2 border-smartify-brand text-smartify-brand'
                  : 'text-smartify-muted hover:text-smartify-ink',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'combinator' ? (
          <EspelhoCombinatorFlow onSave={onSave} onClose={onClose} />
        ) : (
        <>
        {/* Stepper chips (guided) */}
        {mode === 'guided' && (
          <ol className="flex items-center gap-1 overflow-x-auto border-b border-smartify-line px-4 py-2">
            {STEPS.map((s, i) => (
              <li key={s.key} className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  disabled={i > stepIndex}
                  onClick={() => i < stepIndex && setStep(s.key)}
                  className={[
                    'flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
                    i === stepIndex ? 'bg-smartify-brand text-white'
                      : i < stepIndex ? 'bg-smartify-brand/10 text-smartify-brand hover:bg-smartify-brand/20'
                      : 'bg-smartify-paper text-smartify-muted',
                  ].join(' ')}
                >
                  <span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-white/30 text-[9px]">{i + 1}</span>
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <span className="text-smartify-line">·</span>}
              </li>
            ))}
          </ol>
        )}

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {mode === 'browse' ? (
            <BrowseMode catalog={catalog} onAdd={handleBrowseAdd} />
          ) : err ? (
            <div className="rounded-smartify border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
          ) : !cat ? (
            <div className="rounded-smartify border border-smartify-line bg-white p-8 text-center text-sm text-smartify-muted">A carregar catálogo…</div>
          ) : (
            <>
              {step === 'tamanho' && (
                <div>
                  <p className="mb-1 text-sm text-smartify-muted">
                    Quantas posições de aparelhagem? (cada posição leva 1 módulo — botões, estores, cortinas…)
                  </p>
                  <div className="mb-3">
                    <InfoHint label="«Posição», «espelho», «módulo»?">
                      O <b>espelho</b> é a placa visível do interruptor na parede. Cada <b>posição</b>
                      {' '}(ou caixa de aparelhagem) é um lugar onde encaixa um <b>módulo</b> — o
                      mecanismo que faz a função (botão, regulador, estores…). Conta quantos
                      interruptores/lugares queres nessa zona da parede.
                    </InfoHint>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {sizesAvailable(cat).map((s) => (
                      <Card key={s} active={size === s} onClick={() => pickSize(s)}
                        ariaLabel={`${SIZE_LABEL[s]} — ${s} ${s === 1 ? 'posição' : 'posições'}`}>
                        <div className="flex flex-col items-center gap-2 py-2">
                          <SlotGlyph n={s} />
                          <span className="font-semibold text-smartify-ink">{SIZE_LABEL[s]}</span>
                          <span className="text-xs text-smartify-muted">{s} {s === 1 ? 'posição' : 'posições'}</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {step === 'acabamento' && (
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {families.map((f) => (
                    <Card key={f.baseSku} active={baseSku === f.baseSku} onClick={() => pickFamily(f)} ariaLabel={f.configLabel}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium capitalize text-smartify-ink">{f.configLabel}</p>
                          <p className="mt-0.5 flex flex-wrap gap-1 text-[11px] text-smartify-muted">
                            {f.slotFunctions.map((s, i) => (
                              <span key={i} className="rounded bg-smartify-paper px-1.5 py-px">{SLOT_LABEL[s]}</span>
                            ))}
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          {f.colors.map((c) => (
                            <span key={c} className="h-3.5 w-3.5 rounded-full border border-smartify-line" style={{ background: swatchHex(c) }} title={c} />
                          ))}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {step === 'tecnologia' && family && (
                <div>
                  <p className="mb-3 text-sm text-smartify-muted">Protocolo de comunicação dos módulos.</p>
                  {techs.length === 0 ? (
                    <p className="text-sm text-smartify-muted">Este acabamento usa apenas acessórios passivos.</p>
                  ) : (
                    <>
                      <NeutralQuestion />
                      <div className="grid gap-2.5 sm:grid-cols-2">
                        {techs.map((t) => {
                          // P0-2: a "com neutro" tech is incompatible unless the
                          // customer explicitly confirmed a neutral. Block on 'no',
                          // 'unknown' AND unanswered (null) — the safe direction —
                          // instead of only 'no', which let the default slip through.
                          const blocked = techNeedsNeutral(t) && hasNeutral !== 'yes';
                          return (
                            <Card key={t} active={tech === t} disabled={blocked} onClick={() => !blocked && pickTech(t)} ariaLabel={TECH_LABEL[t]}>
                              <div className={blocked ? 'opacity-50' : ''}>
                                <p className="font-medium text-smartify-ink">{TECH_LABEL[t]}</p>
                                <p className="mt-0.5 text-xs text-smartify-muted">{techHint(t)}</p>
                                {techNeedsHub(t) && (
                                  <div className="mt-1">
                                    <InfoHint label="O que é o hub?">
                                      O hub Zigbee é um pequeno aparelho central que liga os módulos Zigbee à
                                      tua rede. Compras uma vez e serve toda a casa. É adicionado ao orçamento
                                      automaticamente quando escolhes Zigbee.
                                    </InfoHint>
                                  </div>
                                )}
                              </div>
                              {blocked && (
                                <p className="mt-1 text-[11px] font-medium text-amber-700">
                                  {hasNeutral === 'no'
                                    ? 'Indisponível — requer neutro (disseste que não tens).'
                                    : 'Requer neutro — confirma acima se a tua instalação tem neutro.'}
                                </p>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {step === 'modulo' && family && tech && (
                <div className="space-y-4">
                  <p className="text-sm text-smartify-muted">Escolhe um módulo por posição.</p>
                  {family.slotFunctions.map((slot, idx) => {
                    const opts = modulesForSlot(cat, family, idx, tech, colorForResolve);
                    return (
                      <div key={idx}>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
                          Posição {idx + 1} · {SLOT_LABEL[slot]}
                        </p>
                        {opts.length === 0 ? (
                          <p className="text-xs text-smartify-muted">Sem módulos compatíveis para esta tecnologia.</p>
                        ) : (
                          <div className="grid gap-2 sm:grid-cols-2">
                            {opts.map((m) => (
                              <Card key={m.sku} active={moduleSkus[idx] === m.sku} onClick={() => setModuleForSlot(idx, m.sku)} compact ariaLabel={m.name}>
                                <p className="text-sm text-smartify-ink">{m.name}</p>
                                {m.notes && <p className="mt-0.5 text-[11px] text-smartify-warn">{m.notes}</p>}
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {step === 'cor' && family && (
                <div>
                  <p className="mb-3 text-sm text-smartify-muted">Acabamento do espelho.</p>
                  <div className="grid grid-cols-3 gap-3">
                    {COLORS.filter((c) => family.colors.includes(c)).map((c) => (
                      <Card key={c} active={color === c} onClick={() => pickColor(c)} ariaLabel={c}>
                        <div className="flex flex-col items-center gap-2 py-1">
                          <span className="h-12 w-12 rounded-full border-2 border-white shadow-card" style={{ background: swatchHex(c) }} />
                          <span className="text-sm font-medium text-smartify-ink">{c}</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {step === 'quantidade' && family && color && (
                <div>
                  <div className="rounded-smartify border border-smartify-line bg-white p-4">
                    <p className="font-medium capitalize text-smartify-ink">{family.configLabel} · {color}</p>
                    <p className="mt-0.5 text-xs text-smartify-muted">
                      {tech ? TECH_LABEL[tech] : ''} · {family.slotFunctions.length} {family.slotFunctions.length === 1 ? 'módulo' : 'módulos'}
                    </p>
                    <div className="mt-4 flex items-center gap-4">
                      <Stepper value={qty} onChange={setQty} />
                      <span className="text-sm text-smartify-muted">unidade(s)</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer (guided only) */}
        {mode === 'guided' && cat && !err && (
          <div className="border-t border-smartify-line p-4">
            {step === 'quantidade' ? (
              <button
                type="button"
                onClick={confirmUnit}
                className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90"
              >
                Adicionar à divisão
              </button>
            ) : step === 'modulo' || step === 'cor' ? (
              <button
                type="button"
                disabled={!canContinue}
                onClick={() => setStep(step === 'modulo' ? 'cor' : 'quantidade')}
                className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-40"
              >
                Continuar →
              </button>
            ) : (
              // tamanho / acabamento / tecnologia advance on selection
              <p className="text-center text-xs text-smartify-muted">Escolhe uma opção para continuar.</p>
            )}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

// ─── Small presentational helpers ─────────────────────────────────────────────

function Card({ active, onClick, children, compact, disabled, ariaLabel }: {
  active?: boolean; onClick: () => void; children: React.ReactNode; compact?: boolean; disabled?: boolean; ariaLabel?: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-disabled={disabled}
      aria-label={ariaLabel} aria-pressed={ariaLabel ? active === true : undefined}
      className={[
        'rounded-smartify border text-left transition-colors',
        compact ? 'p-2.5' : 'p-3.5',
        disabled ? 'cursor-not-allowed border-smartify-line bg-smartify-paper'
          : active ? 'border-smartify-brand bg-smartify-brand/5 ring-1 ring-smartify-brand'
            : 'border-smartify-line bg-white hover:border-smartify-brand/50',
      ].join(' ')}>
      {children}
    </button>
  );
}

function Stepper({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))}
        className="grid h-9 w-9 place-items-center rounded-smartify border border-smartify-line text-lg text-smartify-ink hover:border-smartify-brand">−</button>
      <span className="w-8 text-center text-lg font-semibold text-smartify-ink">{value}</span>
      <button type="button" onClick={() => onChange(value + 1)}
        className="grid h-9 w-9 place-items-center rounded-smartify border border-smartify-line text-lg text-smartify-ink hover:border-smartify-brand">+</button>
    </div>
  );
}

function SlotGlyph({ n }: { n: number }) {
  return (
    <span className="flex gap-0.5 rounded border border-smartify-line bg-smartify-paper p-1">
      {Array.from({ length: n }, (_, i) => (
        <span key={i} className="h-6 w-2.5 rounded-sm bg-smartify-brand/30" />
      ))}
    </span>
  );
}
